import { InfoCircleOutlined } from '@ant-design/icons';
import { Popover, Tooltip } from 'antd';
import { FC, useState } from 'react';

import { BRAND_COLORS } from '../../../../constants/styles';

const MOBILE_BREAKPOINT = 768;

const MobileSplitTabIcon: FC<{ text: string; bgColor?: string }> = ({
  text,
  bgColor = BRAND_COLORS.secondary.steelBlue,
}) => (
  <Popover
    content={
      <span
        style={{
          maxWidth: 220,
          display: 'block',
          color: BRAND_COLORS.secondary.cloud,
        }}
      >
        {text}
      </span>
    }
    trigger="click"
    placement="bottom"
    overlayStyle={{ maxWidth: 240 }}
    overlayInnerStyle={{
      backgroundColor: BRAND_COLORS.secondary.steelBlue,
      color: BRAND_COLORS.secondary.cloud,
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'stretch',
        width: 24,
        marginLeft: -1,
        borderTop: `1px solid ${bgColor}`,
        borderRight: `1px solid ${bgColor}`,
        borderBottom: `1px solid ${bgColor}`,
        borderLeft: 'none',
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        backgroundColor: bgColor,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <InfoCircleOutlined
        style={{ color: BRAND_COLORS.secondary.cloud, fontSize: 11 }}
      />
    </span>
  </Popover>
);

export interface ITabLinkButtonProps {
  onClick?: () => void;
  fontSize?: number;
  iconColor?: string;
  icon: React.ReactNode;
  label: string;
  /** Background color when not hovered (default: #0b345f) */
  bgColor?: string;
  /** Background color when hovered (default: #1a5a8a) */
  hoverBgColor?: string;
  /** Variant for different visual styles */
  variant?: 'dark' | 'light';
  /** Tooltip text to display on hover */
  tooltip?: string;
  /** Current window width — used to switch between hover tooltips and tap popovers */
  windowWidth?: number;
}

export const TabLinkButton: FC<ITabLinkButtonProps> = ({
  onClick,
  fontSize = 11,
  iconColor = BRAND_COLORS.secondary.lightSage,
  icon,
  label,
  bgColor = BRAND_COLORS.secondary.steelBlue,
  hoverBgColor = '#1a5a8a',
  variant = 'dark',
  tooltip,
  windowWidth,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const isDark = variant === 'dark';
  const isMobile = (windowWidth ?? window.innerWidth) <= MOBILE_BREAKPOINT;

  const buttonInner = (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        background: isHovered ? hoverBgColor : bgColor,
        padding: '4px 12px',
        borderRadius: isMobile && tooltip ? '4px 0 0 4px' : 4,
        borderRight:
          isMobile && tooltip ? '1px solid rgba(255,255,255,0.6)' : undefined,
        boxShadow: isPressed
          ? '0 1px 2px rgba(0,0,0,0.2)'
          : '0 2px 4px rgba(0,0,0,0.25)',
        transform: isPressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'all 0.15s ease',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      <span style={{ fontSize: fontSize + 3, color: iconColor }}>{icon}</span>
      <span style={{ color: isDark ? '#fff' : '#2f3a45', fontSize }}>
        {label}
      </span>
    </div>
  );

  if (tooltip) {
    if (isMobile) {
      return (
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {buttonInner}
            <MobileSplitTabIcon text={tooltip} bgColor={bgColor} />
          </span>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Tooltip
          title={tooltip}
          overlayInnerStyle={{
            background: '#2f3a45',
            border: `1px solid ${BRAND_COLORS.secondary.steelBlue}`,
            borderRadius: 3,
          }}
          color="#2f3a45"
        >
          {buttonInner}
        </Tooltip>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      {buttonInner}
    </div>
  );
};
